<script setup>
import { ref, watch } from 'vue';
import CSMContactResolution from './CSMContactResolution.vue';

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const context = ref(null);
const errorMessage = ref('');
const loading = ref(false);
const resolving = ref(false);
let requestSequence = 0;

const customerKindLabels = Object.freeze({
  legal: 'Юрлицо',
  physical: 'Физлицо',
});

const stateLabels = Object.freeze({
  assigned: 'Назначено',
  awaitingReply: 'Ожидание ответа',
  cancelled: 'Отменено',
  classification: 'Классификация',
  closed: 'Закрыто',
  deferred: 'Отложено',
  duplicate: 'Дубликат',
  inQueue: 'В очереди',
  inprogress: 'В работе',
  needClar: 'Требуется уточнение',
  registered: 'Зарегистрировано',
  resolved: 'Решено',
  resumed: 'Возобновлено',
});

const fieldLabels = Object.freeze({
  legal_name: 'Полное наименование',
  first_name: 'Имя',
  last_name: 'Фамилия',
  middle_name: 'Отчество',
  email: 'Email',
  phone: 'Телефон',
  tax_id: 'ИНН',
  tax_branch_id: 'КПП',
});

const hiddenCustomerFields = new Set([
  'id',
  'kind',
  'display_name',
  'first_name',
  'last_name',
  'middle_name',
  'email',
  'phone',
]);
const technicalIDPattern = /(?:contactPerson|customer|appeal)\$/;

const fieldLabel = key => {
  if (fieldLabels[key]) return fieldLabels[key];
  const words = key.replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const displayValue = value => {
  if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const customerFields = customer =>
  Object.entries(customer).filter(([key, value]) => {
    if (hiddenCustomerFields.has(key) || value === null || value === '') {
      return false;
    }
    if (typeof value === 'object' && !Array.isArray(value)) return false;
    return !technicalIDPattern.test(displayValue(value));
  });

const customerKindLabel = kind => customerKindLabels[kind] || 'Клиент';
const appealStateLabel = state => stateLabels[state] || state;
const contactPersonName = person =>
  [person.last_name, person.first_name, person.middle_name]
    .filter(Boolean)
    .join(' ') || person.display_name;

const loadContext = async () => {
  const currentRequest = ++requestSequence;
  loading.value = true;
  errorMessage.value = '';
  try {
    const query = new URLSearchParams({
      account_id: String(props.accountId),
      conversation_id: String(props.conversationId),
    });
    const response = await fetch(`/csm-action/api/context?${query}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`status ${response.status}`);
    const payload = await response.json();
    if (currentRequest === requestSequence) context.value = payload;
  } catch {
    if (currentRequest === requestSequence) {
      context.value = null;
      errorMessage.value = 'Не удалось загрузить контекст CSM';
    }
  } finally {
    if (currentRequest === requestSequence) loading.value = false;
  }
};

const applyResolvedContext = payload => {
  context.value = payload;
  resolving.value = false;
};

watch(
  [() => props.accountId, () => props.conversationId],
  loadContext,
  { immediate: true }
);
</script>

<template>
  <div data-testid="csm-context" class="csm-context">
    <div v-if="loading" class="csm-context__status">Загрузка контекста…</div>
    <div v-else-if="errorMessage" class="csm-context__status">
      <span>{{ errorMessage }}</span>
      <button type="button" class="csm-context__retry" @click="loadContext">
        Повторить
      </button>
    </div>
    <template v-else-if="context">
      <div v-if="context.sender && !context.links?.length" class="csm-context__sender">
        <span class="csm-context__eyebrow">Пишет</span>
        <strong>{{ context.sender.display_name || 'Контакт Chatwoot' }}</strong>
        <span v-if="context.sender.email">{{ context.sender.email }}</span>
        <span v-if="context.sender.phone">{{ context.sender.phone }}</span>
      </div>

      <div v-if="!resolving && context.links?.length" class="csm-context__links">
        <article
          v-for="link in context.links"
          :key="`${link.contact_person.id}:${link.customer.id}`"
          class="csm-context__card"
        >
          <header class="csm-context__card-header">
            <span class="csm-context__eyebrow">Контактное лицо</span>
            <h4 :title="contactPersonName(link.contact_person)">
              {{ contactPersonName(link.contact_person) }}
            </h4>
            <span v-if="link.contact_person.position">
              {{ link.contact_person.position }}
            </span>
            <span v-else-if="link.contact_person.relationship">
              {{ link.contact_person.relationship }}
            </span>
            <div class="csm-context__customer-line">
              <span class="csm-context__kind">
                {{ customerKindLabel(link.customer.kind) }}
              </span>
              <strong>{{ link.customer.display_name }}</strong>
            </div>
          </header>

          <div
            v-if="link.customer.email || link.customer.phone"
            class="csm-context__channels"
          >
            <a
              v-if="link.customer.email"
              :href="`mailto:${link.customer.email}`"
              :title="link.customer.email"
            >
              {{ link.customer.email }}
            </a>
            <a
              v-if="link.customer.phone"
              :href="`tel:${link.customer.phone}`"
              :title="link.customer.phone"
            >
              {{ link.customer.phone }}
            </a>
          </div>

          <details
            v-if="customerFields(link.customer).length"
            class="csm-context__section"
            data-testid="customer-details"
          >
            <summary>
              <span>Данные клиента</span>
              <span class="csm-context__count">
                {{ customerFields(link.customer).length }}
              </span>
            </summary>
            <dl class="csm-context__fields">
              <div
              v-for="[key, value] in customerFields(link.customer)"
              :key="key"
              >
                <dt>{{ fieldLabel(key) }}</dt>
                <dd>{{ displayValue(value) }}</dd>
              </div>
            </dl>
          </details>

          <details class="csm-context__section" data-testid="appeal-history">
            <summary>
              <span>История обращений</span>
              <span class="csm-context__count">{{ link.appeals?.length || 0 }}</span>
            </summary>
            <div class="csm-context__history">
              <ul v-if="link.appeals?.length">
                <li v-for="appeal in link.appeals" :key="appeal.id">
                  <div>
                    <strong>{{ appeal.display_number || 'Обращение' }}</strong>
                    <span class="csm-context__state">
                      {{ appealStateLabel(appeal.state) }}
                    </span>
                  </div>
                  <p>{{ appeal.subject }}</p>
                </li>
              </ul>
              <p v-else class="csm-context__empty">Обращений пока нет</p>
            </div>
          </details>
        </article>
      </div>
      <p v-else-if="!resolving" class="csm-context__status">
        Контактное лицо не определено автоматически
      </p>

      <CSMContactResolution
        v-if="resolving"
        :account-id="accountId"
        :conversation-id="conversationId"
        :sender="context.sender"
        @resolved="applyResolvedContext"
        @cancel="resolving = false"
      />
      <button
        v-else
        type="button"
        class="csm-context__resolve"
        data-testid="open-contact-resolution"
        @click="resolving = true"
      >
        <span aria-hidden="true">+</span>
        {{ context.links?.length ? 'Добавить связь' : 'Найти или создать контактное лицо' }}
      </button>
    </template>
  </div>
</template>

<style scoped src="./CSMContextPanel.css"></style>
